import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';

import { GridRowParams, MuiEvent } from '@mui/x-data-grid-pro';
import { useSearchParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

import { useColumns } from './hooks/useColumns';
import { PROJECT_ID_PARAMETER_NAME } from '../../constants';
import { Project } from '@/api/typesense/projects';
import {
  BaseDataTable,
  BaseDataTableProps,
} from '@/components/ui/BaseDataTable';

type ProjectTableProps = Omit<BaseDataTableProps<Project>, 'getRowId'> & {
  totalResults: number;
  getRowId?: (row: Project) => number;
  isPanelOpen?: boolean;
  onPanelOpen?: () => void;
  onPanelOpenAndFocus?: () => void;
};

export const ProjectTable = ({
  getRowId = (r: Project) => r.SourceId,
  columns: columnsProp,
  columnWidths,
  isPanelOpen = false,
  onPanelOpen,
  onPanelOpenAndFocus,
  totalResults,
  ...restProps
}: ProjectTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get(PROJECT_ID_PARAMETER_NAME);

  // Handle project selection by updating URL params
  const handleViewDetails = (
    projectId: string,
    options?: { replace?: boolean },
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set(PROJECT_ID_PARAMETER_NAME, projectId);
      return newParams;
    }, options);
  };

  const defaultColumns = useColumns({
    columnWidths,
    onViewDetails: handleViewDetails,
  });
  const columns = columnsProp ?? defaultColumns;

  const handleRowClick = (
    params: GridRowParams<Project>,
    event: MuiEvent<ReactMouseEvent<HTMLElement>>,
  ) => {
    // Don't trigger row click when clicking on interactive elements (links, buttons, etc.)
    const target = event.target as HTMLElement;
    if (target.closest('a, button')) {
      return;
    }

    const clickedId = String(params.row.SourceId);

    handleViewDetails(clickedId);
    onPanelOpen?.();
  };

  // Arrow key navigation - only active when panel is open, debounced to prevent API spam
  const handleRowNavigate = useDebouncedCallback(
    (row: Project) => {
      if (!isPanelOpen) return;
      handleViewDetails(String(row.SourceId), { replace: true });
    },
    100, // 100ms debounce to avoid spamming when holding down arrow keys
    { leading: false, trailing: true },
  );

  // Ctrl+Enter and Ctrl+Shift+Enter toggle behavior
  const handleRowToggle = useCallback(
    (row: Project, options?: { shouldFocus: boolean }) => {
      const clickedId = String(row.SourceId);

      handleViewDetails(clickedId);

      if (options?.shouldFocus) {
        if (onPanelOpenAndFocus) {
          onPanelOpenAndFocus();
        } else {
          onPanelOpen?.();
        }
      } else {
        onPanelOpen?.();
      }
    },
    [handleViewDetails, onPanelOpen, onPanelOpenAndFocus],
  );

  return (
    <BaseDataTable<Project>
      getRowId={getRowId}
      onRowClick={handleRowClick}
      onRowNavigate={handleRowNavigate}
      onRowToggle={handleRowToggle}
      columns={columns}
      columnWidths={columnWidths}
      selectedRowId={isPanelOpen ? selectedProjectId : null}
      data-automation-id="project-portfolio-data-grid"
      pinnedColumns={{ left: ['HealthStatusName', 'Name'] }}
      hasRowNavigation
      totalResults={totalResults}
      {...restProps}
    />
  );
};
